/* eslint-disable camelcase */
/* eslint-disable no-else-return */

export default class Tree {
  /** Traverses a tree along a given `child_attr`
   * recursion axis. Perform a depth-first-search (dfs).
   * @param {Tree<A>} tree input tree
   * @param {function<Tree<A>,B>} f mapping from nodes to an arbitary datatype
   * @param {B} accu result accumulator
   * @param {string} child_attr attribute to recurse over
   * @return {B} the accumulated result
   */
  static reduce(tree, f, accu, child_attr = 'children') {
    if (!tree) { return accu; }
    if (tree instanceof Array) {
      if (tree.length === 0) { return accu; }
      const hd = tree[0];
      const tl = tree.slice(1);
      if (!hd) {
        // eslint-disable-next-line no-console
        console.error('Warning: Tree.reduce found malformed tree contained null-child:', tree);
        return Tree.reduce(tl, f, accu, child_attr);
      }
      // proceed dfs
      const accu1 = f(accu, hd);
      const accu_dfs = Tree.reduce(hd[child_attr], f, accu1, child_attr);
      return Tree.reduce(tl, f, accu_dfs, child_attr);
    } else { // tree instanceof Object
      const accu1 = f(accu, tree);
      return Tree.reduce(tree[child_attr], f, accu1, child_attr);
    }
  }

  /** Maps a tree structure to a new tree structure, creating
   * a recursive copy of the tree along the `child_attr` axis.
   * Atttributes that are not recursed over, are not copied.
   * @param {Tree<A>} tree input tree
   * @param {function<Tree<A>,Tree<B>>} f a mapping between nodes
   * @param {string} child_attr attribute to recurse over
   * @return {Tree<B>} the mapped tree
   */
  static map(tree, f, child_attr = 'children') {
    if (!tree) { return tree; }
    function map_node_f(n) {
      const n_next = f(n);
      const children_prev = n_next[child_attr]; // f may do a prefilter on the children
      n_next[child_attr] = Tree.map(children_prev, f, child_attr); // apply recursive map
      return n_next;
    }
    if (tree instanceof Array) {
      return tree.map(map_node_f);
    } else { // tree instanceof Object
      return map_node_f(tree);
    }
  }

  /** Applies a function with side-effects to the tree. The result of the function is forfeit.
   * Use this to in-place modify the tree. Does no copying. The parameter `tree`
   * is passed through `reduce` only as a pointer.
   * NOTE: the parameter order is unusual, and we should probably flip it
   * @param {Tree<A>} tree recursing on 'children'
   * @param {function<Tree<A>,any>} map_f a function with side-effects, whose result is forfeit
   * @return {Tree<A>} input tree with modifications
   */
  static forEach(tree, map_f) {
    return Tree.reduce(tree, (a, e) => { map_f(e); return a; }, tree);
  }

  static some(tree, pred) {
    return Tree.reduce(tree, (a, e) => a || pred(e), false);
  }

  /** Convenient abbreviation for flatting
   * @param {Tree<A>} tree recursing on 'children'
   * @param {function<Tree<A>,Tree<B>>} map_f an extractor that skips creating an
   * intermediary node-pointer list, shortening `Tree.flat(xs).map(f)` to `Tree.flat(xs,f)`
   * @returns {Tree<B>[]} a list of tree nodes
   */
  static flat(tree, map_f = (node) => node) {
    return Tree.reduce(tree, (a, e) => { a.push(map_f(e)); return a; }, []);
    // I played some code golf here. But ended up using the baseline variant,
    // because .push performs no copying.
    // const value_flat = Tree.reduce(value, (a,node) => a.concat([node.code]), []);
    // works, but creates a copy
    // const value_flat = Tree.reduce_tree(value, Array.prototype.concat.bind, []);
    // `.bind` does partial application
    // const value_flat = Tree.reduce_tree(value, [].concat.bind, []);
    // doesn't work because bind returns a lambda, not an array
    // const value_flat = Tree.reduce(value, ae => [].concat.bind(ae)(), []);
    // doesn't work either
  }
}
